import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getAppointments, updateAppointment, getAvailableSlots, getSchedule, createAdminAppointment, getAdminCalendarEvents } from '../lib/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import {
  CheckCircle,
  CircleCheck,
  XCircle,
  Clock,
  Calendar as CalendarIconLucide,
  User,
  Phone,
  Mail,
  Plus,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { es } from 'date-fns/locale';
import { useMexicoCityClock } from '../hooks/useMexicoCityClock';
import {
  formatUtcIsoInClinic,
  calendarDayKeyInClinic,
  formatAppointmentDateOnlyInClinic,
  todayYmdInClinic,
  getCalendarApiRangeUtc,
  addCalendarDaysYmd,
  addCalendarMonthsYmd,
  isoWeekBoundsYmd,
  monthGridBoundsYmd,
  eachYmdInclusive,
  formatYmdClinic,
  ymdToDatePickerDate,
  firstOfMonthYmd,
  calendarIsoWeekdayFromYmd,
} from '../lib/clinicTime';

const initialNewAppointmentForm = {
  patient_name: '',
  patient_age: '',
  patient_email: '',
  patient_phone: '',
  reason: '',
  notes: '',
};

/** Alineado con el backend: al menos 3 partes (nombre(s) + apellidos), cada una ≥ 2 caracteres. */
const validateMexicanFullName = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length < 3) return false;
  return parts.every((p) => p.length >= 2);
};

const parsePatientAgeYears = (raw) => {
  const n = parseInt(String(raw ?? '').trim(), 10);
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > 120) return null;
  return n;
};

const DEFAULT_CALENDAR_SYNC_MS = 30000;
const rawSyncMs = Number(process.env.REACT_APP_CALENDAR_SYNC_MS);
const CALENDAR_SYNC_MS = Number.isFinite(rawSyncMs) && rawSyncMs >= 5000 ? rawSyncMs : DEFAULT_CALENDAR_SYNC_MS;

const areCalendarEventsEqual = (prevEvents, nextEvents) => {
  if (prevEvents === nextEvents) return true;
  if (!Array.isArray(prevEvents) || !Array.isArray(nextEvents)) return false;
  if (prevEvents.length !== nextEvents.length) return false;
  for (let i = 0; i < prevEvents.length; i += 1) {
    const a = prevEvents[i];
    const b = nextEvents[i];
    if (
      a?.id !== b?.id ||
      a?.source !== b?.source ||
      a?.title !== b?.title ||
      a?.status !== b?.status ||
      a?.start_at !== b?.start_at ||
      a?.end_at !== b?.end_at ||
      a?.description !== b?.description
    ) {
      return false;
    }
  }
  return true;
};

export default function AdminAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsAvailable, setSlotsAvailable] = useState(true);
  const [slotsReason, setSlotsReason] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newApptForm, setNewApptForm] = useState(initialNewAppointmentForm);
  const [calendarView, setCalendarView] = useState('month');
  const [focusYmd, setFocusYmd] = useState(() => todayYmdInClinic());
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [selectedCalendarYmd, setSelectedCalendarYmd] = useState(() => todayYmdInClinic());

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState({ id: null, patientName: '' });
  const [cancelMessage, setCancelMessage] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const mxClock = useMexicoCityClock();

  const getCalendarRange = useCallback(() => {
    return getCalendarApiRangeUtc(focusYmd, calendarView);
  }, [focusYmd, calendarView]);

  const loadAppointments = useCallback(async () => {
    try {
      const status = statusFilter === 'all' ? null : statusFilter;
      const data = await getAppointments(status);
      setAppointments(data);
    } catch (error) {
      toast.error('Error al cargar citas');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const loadCalendarEvents = useCallback(async (options = {}) => {
    const silent = Boolean(options.silent);
    if (!silent) setLoadingCalendar(true);
    try {
      const range = getCalendarRange();
      const data = await getAdminCalendarEvents(range.start.toISOString(), range.end.toISOString());
      const nextEvents = data.events || [];
      setCalendarEvents((prev) => (areCalendarEventsEqual(prev, nextEvents) ? prev : nextEvents));
    } catch (error) {
      if (!silent) {
        toast.error('No se pudieron cargar eventos del calendario');
      }
    } finally {
      if (!silent) setLoadingCalendar(false);
    }
  }, [getCalendarRange]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    loadCalendarEvents();
  }, [loadCalendarEvents]);

  useEffect(() => {
    // Auto-sync periódico para reflejar cambios hechos directamente en Google Calendar.
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadCalendarEvents({ silent: true });
      }
    }, CALENDAR_SYNC_MS);
    return () => clearInterval(timer);
  }, [loadCalendarEvents]);

  useEffect(() => {
    if (!newDialogOpen) return;
    const loadSchedule = async () => {
      try {
        const data = await getSchedule();
        setSchedule(data);
      } catch {
        toast.error('No se pudo cargar el horario del consultorio');
      }
    };
    loadSchedule();
  }, [newDialogOpen]);

  useEffect(() => {
    if (!newDialogOpen || !selectedDate) return;
    const loadSlots = async () => {
      setLoadingSlots(true);
      try {
        const dateStr = selectedDate ? calendarDayKeyInClinic(selectedDate) : '';
        if (!dateStr) return;
        const data = await getAvailableSlots(dateStr);
        setAvailableSlots(data.slots || []);
        setSlotsAvailable(data.available);
        setSlotsReason(data.reason || '');
      } catch {
        setAvailableSlots([]);
        toast.error('Error al cargar horarios disponibles');
      } finally {
        setLoadingSlots(false);
      }
    };
    loadSlots();
  }, [newDialogOpen, selectedDate]);

  const resetNewAppointmentDialog = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setAvailableSlots([]);
    setSlotsAvailable(true);
    setSlotsReason('');
    setNewApptForm(initialNewAppointmentForm);
  };

  const isDateDisabled = (date) => {
    const ymd = calendarDayKeyInClinic(date);
    if (!ymd) return true;
    if (ymd < todayYmdInClinic()) return true;
    if (!schedule) return false;
    const isoDay = calendarIsoWeekdayFromYmd(ymd);
    if (!Number.isFinite(isoDay)) return true;
    return !schedule.working_days.includes(isoDay);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) {
      toast.error('Selecciona fecha y hora');
      return;
    }
    if (!validateMexicanFullName(newApptForm.patient_name)) {
      toast.error(
        'Indique el nombre completo: nombre(s) de pila, apellido paterno y apellido materno (cada parte con al menos 2 caracteres).',
      );
      return;
    }
    const ageYears = parsePatientAgeYears(newApptForm.patient_age);
    if (ageYears == null) {
      toast.error('Indique una edad válida entre 1 y 120 años.');
      return;
    }
    setCreating(true);
    try {
      const emailTrim = newApptForm.patient_email.trim();
      const payload = {
        patient_name: newApptForm.patient_name.trim(),
        patient_age: ageYears,
        patient_phone: newApptForm.patient_phone,
        reason: newApptForm.reason,
        notes: newApptForm.notes,
        date: calendarDayKeyInClinic(selectedDate) || '',
        time: selectedTime,
      };
      if (emailTrim) payload.patient_email = emailTrim;
      await createAdminAppointment(payload);
      toast.success('Cita creada');
      setNewDialogOpen(false);
      resetNewAppointmentDialog();
      loadAppointments();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear la cita';
      toast.error(typeof message === 'string' ? message : 'Error al crear la cita');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateAppointment(id, { status: newStatus });
      const msg =
        newStatus === 'confirmed'
          ? 'Cita confirmada'
          : newStatus === 'completed'
            ? 'Cita marcada como completada'
            : newStatus === 'cancelled'
              ? 'Cita cancelada'
              : 'Cita actualizada';
      toast.success(msg);
      loadAppointments();
      loadCalendarEvents();
    } catch (error) {
      toast.error('Error al actualizar cita');
    }
  };

  const openCancelDialog = (appointmentId, patientName = '') => {
    setCancelTarget({ id: appointmentId, patientName });
    const first = patientName ? String(patientName).trim().split(/\s+/)[0] : '';
    setCancelMessage(
      `Hola${first ? ` ${first}` : ''}, lamentamos informarte que tu cita en PodoclinicAM quedó cancelada. Si deseas otra fecha, escríbenos.`,
    );
    setCancelDialogOpen(true);
  };

  const submitCancelAppointment = async () => {
    if (!cancelTarget.id) return;
    setCancelSubmitting(true);
    try {
      await updateAppointment(cancelTarget.id, {
        status: 'cancelled',
        cancel_whatsapp_message: cancelMessage.trim(),
      });
      toast.success('Cita cancelada');
      setCancelDialogOpen(false);
      setCancelTarget({ id: null, patientName: '' });
      loadAppointments();
      loadCalendarEvents();
    } catch (error) {
      toast.error('Error al cancelar la cita');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const resolveCalendarAppointmentId = (event) => {
    if (event.appointment?.id) return event.appointment.id;
    if (event.linked_appointment_id) return String(event.linked_appointment_id).trim();
    const desc = event.description || '';
    const m = desc.match(/Appointment ID:\s*([a-fA-F0-9-]{10,})/i);
    return m ? m[1].trim() : null;
  };

  const getCalendarEventSourceLabel = (event) => {
    if (event.source === 'google') {
      return resolveCalendarAppointmentId(event) ? 'Agenda (Google)' : 'Solo Google';
    }
    const src = event.appointment?.booking_source;
    if (src === 'admin') return 'Panel admin';
    if (src === 'whatsapp') return 'WhatsApp';
    return 'Sitio web';
  };

  const getDbPatientName = (event) =>
    (event.appointment?.patient_name && String(event.appointment.patient_name).trim()) ||
    (event.title && String(event.title).replace(/^Cita\s*-\s*/i, '').trim()) ||
    '';

  const renderDbCalendarActions = (event) => {
    const apptId = resolveCalendarAppointmentId(event);
    if (!apptId) return null;
    const apptSt = event.appointment?.status;
    if (apptSt === 'cancelled' || apptSt === 'completed') return null;
    const st = apptSt ?? (event.source === 'google' ? null : event.status);
    const patientName = getDbPatientName(event);
    const isGoogleOnly = event.source === 'google';
    return (
      <div className="mt-3 flex flex-wrap gap-2">
        {!isGoogleOnly && st === 'pending' && (
          <>
            <Button type="button" size="sm" onClick={() => handleStatusChange(apptId, 'confirmed')}>
              <CheckCircle className="w-4 h-4 mr-1" />
              Confirmar
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => openCancelDialog(apptId, patientName)}>
              <XCircle className="w-4 h-4 mr-1" />
              Cancelar
            </Button>
          </>
        )}
        {!isGoogleOnly && st === 'confirmed' && (
          <Button type="button" size="sm" variant="outline" onClick={() => openCancelDialog(apptId, patientName)}>
            <XCircle className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
        )}
        {isGoogleOnly && (
          <Button type="button" size="sm" variant="outline" onClick={() => openCancelDialog(apptId, patientName)}>
            <XCircle className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
        )}
        <Button type="button" size="sm" variant="secondary" onClick={() => handleStatusChange(apptId, 'completed')}>
          <CircleCheck className="w-4 h-4 mr-1" />
          Marcar completada
        </Button>
      </div>
    );
  };

  const moveCalendar = (direction) => {
    if (calendarView === 'day') {
      setFocusYmd((prev) => addCalendarDaysYmd(prev, direction));
      return;
    }
    if (calendarView === 'week') {
      setFocusYmd((prev) => addCalendarDaysYmd(prev, direction * 7));
      return;
    }
    setFocusYmd((prev) => addCalendarMonthsYmd(prev, direction));
  };

  const eventsForDay = (ymd) =>
    calendarEvents
      .filter((event) => {
        try {
          return calendarDayKeyInClinic(event.start_at) === ymd;
        } catch {
          return false;
        }
      })
      .sort((a, b) => a.start_at.localeCompare(b.start_at));

  const selectedDayEvents = eventsForDay(selectedCalendarYmd);

  const getEventColor = (event) => {
    if (event.source === 'google') return 'bg-primary/15 border-primary/30 text-primary';
    const kind = event.appointment_kind || event.appointment?.appointment_kind;
    if (kind === 'review') return 'bg-violet-500/15 border-violet-500/35 text-violet-700 dark:text-violet-300';
    const status = event.status || 'pending';
    if (status === 'confirmed') return 'bg-success/15 border-success/30 text-success';
    if (status === 'completed') return 'bg-primary/15 border-primary/30 text-primary';
    return 'bg-warning/15 border-warning/30 text-warning';
  };

  const renderMonthGrid = () => {
    const { gridStartYmd, gridEndYmd } = monthGridBoundsYmd(focusYmd);
    const days = eachYmdInclusive(gridStartYmd, gridEndYmd);
    const weekdayLabels = [0, 1, 2, 3, 4, 5, 6].map((i) => formatYmdClinic(addCalendarDaysYmd(gridStartYmd, i), 'EEEE'));

    return (
      <div className="space-y-2">
        <div
          className="grid grid-cols-7 gap-2"
          aria-hidden
        >
          {weekdayLabels.map((label, i) => (
            <div
              key={`month-wd-${i}`}
              className="text-center text-xs font-semibold capitalize text-muted-foreground px-0.5 py-1.5 border-b border-border/70"
            >
              {label}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
        {days.map((ymd) => {
          const items = eventsForDay(ymd);
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => setSelectedCalendarYmd(ymd)}
              className={`min-h-[130px] rounded-lg border p-2 text-left transition ${
                ymd === selectedCalendarYmd ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <div className="text-xs font-medium mb-2">{formatYmdClinic(ymd, 'd MMM')}</div>
              <div className="space-y-1">
                {items.slice(0, 3).map((event) => (
                  <Tooltip key={`${event.source}-${event.id}`}>
                    <TooltipTrigger asChild>
                      <div className={`rounded border px-2 py-1 text-[11px] truncate ${getEventColor(event)}`}>
                        {formatUtcIsoInClinic(event.start_at, 'HH:mm')} - {event.title}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold">{event.title}</p>
                      <p>{formatUtcIsoInClinic(event.start_at, 'PPP p')}</p>
                      <p>{event.description || 'Sin descripción'}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
                {items.length > 3 ? <p className="text-[11px] text-muted-foreground">+{items.length - 3} más</p> : null}
              </div>
            </button>
          );
        })}
        </div>
      </div>
    );
  };

  const renderWeekGrid = () => {
    const { weekStartYmd, weekEndYmd } = isoWeekBoundsYmd(focusYmd);
    const days = eachYmdInclusive(weekStartYmd, weekEndYmd);
    return (
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((ymd) => {
          const items = eventsForDay(ymd);
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => setSelectedCalendarYmd(ymd)}
              className={`rounded-lg border p-3 text-left ${
                ymd === selectedCalendarYmd ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <p className="font-medium text-sm mb-2">{formatYmdClinic(ymd, 'EEEE d')}</p>
              <div className="space-y-1">
                {items.length === 0 ? <p className="text-xs text-muted-foreground">Sin citas</p> : null}
                {items.map((event) => (
                  <Tooltip key={`${event.source}-${event.id}`}>
                    <TooltipTrigger asChild>
                      <div className={`rounded border px-2 py-1 text-xs truncate ${getEventColor(event)}`}>
                        {formatUtcIsoInClinic(event.start_at, 'HH:mm')} - {event.title}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-semibold">{event.title}</p>
                      <p>{event.description || 'Sin descripción'}</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-warning/10 text-warning border-warning/20',
      confirmed: 'bg-success/10 text-success border-success/20',
      cancelled: 'bg-accent/10 text-accent border-accent/20',
      completed: 'bg-primary/10 text-primary border-primary/20'
    };
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Completada'
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Cargando citas...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-appointments">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span>Citas</span>
            <span
              className="text-sm sm:text-base font-mono font-semibold text-muted-foreground tracking-tight"
              title="Fecha y hora en Ciudad de México (centro), actualización cada segundo"
            >
              {mxClock.fullLabel}
            </span>
          </h1>
          <p className="text-muted-foreground">Gestiona las citas del consultorio</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Dialog
            open={newDialogOpen}
            onOpenChange={(open) => {
              setNewDialogOpen(open);
              if (!open) resetNewAppointmentDialog();
            }}
          >
            <DialogTrigger asChild>
              <Button className="rounded-full gap-2" data-testid="admin-new-appointment-btn">
                <Plus className="w-4 h-4" />
                Nueva cita
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Crear cita (administrador)</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <CalendarIconLucide className="w-4 h-4 text-primary" />
                      Fecha
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(d) => {
                        setSelectedDate(d);
                        setSelectedTime(null);
                      }}
                      disabled={isDateDisabled}
                      fromDate={ymdToDatePickerDate(todayYmdInClinic())}
                      toDate={ymdToDatePickerDate(addCalendarDaysYmd(todayYmdInClinic(), 60))}
                      locale={es}
                      className="rounded-xl border mx-auto"
                      data-testid="admin-new-appt-calendar"
                    />
                  </CardContent>
                </Card>
                <Card className="border border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Hora
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!selectedDate ? (
                      <p className="text-muted-foreground text-sm text-center py-6">Elige una fecha</p>
                    ) : loadingSlots ? (
                      <p className="text-muted-foreground text-sm text-center py-6 animate-pulse">Cargando…</p>
                    ) : !slotsAvailable ? (
                      <div className="text-center text-sm text-muted-foreground py-4">
                        <p>No hay horarios</p>
                        {slotsReason ? <p className="mt-1">{slotsReason}</p> : null}
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <p className="text-muted-foreground text-sm text-center py-6">Sin cupos este día</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 max-h-[220px] overflow-y-auto">
                        {availableSlots.map((time) => (
                          <button
                            key={time}
                            type="button"
                            onClick={() => setSelectedTime(time)}
                            className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                              selectedTime === time
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-sage-50 text-foreground hover:bg-sage-100'
                            }`}
                            data-testid={`admin-new-appt-slot-${time}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              <form onSubmit={handleCreateAppointment} className="space-y-3 mt-4" data-testid="admin-new-appointment-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="admin-appt-name" className="flex items-center gap-1">
                      <User className="w-3 h-3" /> Nombre completo *
                    </Label>
                    <Input
                      id="admin-appt-name"
                      value={newApptForm.patient_name}
                      onChange={(e) => setNewApptForm({ ...newApptForm, patient_name: e.target.value })}
                      required
                      placeholder="Ej. Ana María López García"
                      className="mt-1 h-10"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Nombre(s), apellido paterno y apellido materno (mínimo tres palabras).
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="admin-appt-phone" className="flex items-center gap-1">
                      <Phone className="w-3 h-3" /> Teléfono *
                    </Label>
                    <Input
                      id="admin-appt-phone"
                      value={newApptForm.patient_phone}
                      onChange={(e) => setNewApptForm({ ...newApptForm, patient_phone: e.target.value })}
                      required
                      className="mt-1 h-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="admin-appt-age">Edad (años) *</Label>
                  <Input
                    id="admin-appt-age"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={120}
                    value={newApptForm.patient_age}
                    onChange={(e) => setNewApptForm({ ...newApptForm, patient_age: e.target.value })}
                    required
                    className="mt-1 h-10 max-w-[140px]"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-appt-email" className="flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Email (opcional)
                  </Label>
                  <Input
                    id="admin-appt-email"
                    type="email"
                    value={newApptForm.patient_email}
                    onChange={(e) => setNewApptForm({ ...newApptForm, patient_email: e.target.value })}
                    className="mt-1 h-10"
                  />
                </div>
                <div>
                  <Label htmlFor="admin-appt-reason" className="flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Motivo
                  </Label>
                  <Textarea
                    id="admin-appt-reason"
                    value={newApptForm.reason}
                    onChange={(e) => setNewApptForm({ ...newApptForm, reason: e.target.value })}
                    rows={2}
                    className="mt-1"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={creating}>
                  {creating ? 'Guardando…' : 'Crear cita'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="confirmed">Confirmadas</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
              <SelectItem value="completed">Completadas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border border-border">
        <CardHeader className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <CardTitle className="font-heading text-xl">Calendario de citas (Google + Web)</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant={calendarView === 'day' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('day')}>Día</Button>
              <Button variant={calendarView === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('week')}>Semana</Button>
              <Button variant={calendarView === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setCalendarView('month')}>Mes</Button>
              <Button variant="outline" size="icon" onClick={() => moveCalendar(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const t = todayYmdInClinic();
                  setFocusYmd(t);
                  setSelectedCalendarYmd(t);
                }}
              >
                Hoy
              </Button>
              <Button variant="outline" size="icon" onClick={() => moveCalendar(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={loadCalendarEvents} disabled={loadingCalendar}>
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingCalendar ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Vista actual:{' '}
            {calendarView === 'day'
              ? formatYmdClinic(focusYmd, 'PPP')
              : calendarView === 'week'
                ? `Semana de ${formatYmdClinic(isoWeekBoundsYmd(focusYmd).weekStartYmd, 'd MMM')}`
                : formatYmdClinic(firstOfMonthYmd(focusYmd), 'MMMM yyyy')}
          </p>
          {loadingCalendar ? (
            <p className="text-xs text-muted-foreground">Sincronizando calendario…</p>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          <TooltipProvider>
            {calendarView === 'month' ? (
              renderMonthGrid()
            ) : calendarView === 'week' ? (
              renderWeekGrid()
            ) : (
              <div className="space-y-2">
                {eventsForDay(focusYmd).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No hay citas para este día.</p>
                ) : (
                  eventsForDay(focusYmd).map((event) => (
                    <div key={`${event.source}-${event.id}`} className={`rounded-lg border p-3 ${getEventColor(event)}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-medium">{event.title}</p>
                        <Badge variant="outline">{getCalendarEventSourceLabel(event)}</Badge>
                      </div>
                      <p className="text-sm">{formatUtcIsoInClinic(event.start_at, 'PPP p')}</p>
                      <p className="text-sm mt-1">{event.description || 'Sin descripción'}</p>
                      {renderDbCalendarActions(event)}
                    </div>
                  ))
                )}
              </div>
            )}
          </TooltipProvider>

          {calendarView !== 'day' ? (
            <Card className="border border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  Detalle del día: {formatYmdClinic(selectedCalendarYmd, "EEEE d 'de' MMMM yyyy")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDayEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin citas para este día.</p>
                ) : (
                  <div className="space-y-2">
                    {selectedDayEvents.map((event) => (
                      <div key={`detail-${event.source}-${event.id}`} className={`rounded-lg border p-3 ${getEventColor(event)}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-semibold">{event.title}</p>
                          <Badge variant="outline">{getCalendarEventSourceLabel(event)}</Badge>
                        </div>
                        <p className="text-sm">{formatUtcIsoInClinic(event.start_at, 'p')}</p>
                        <p className="text-sm text-muted-foreground mt-1">{event.description || 'Sin descripción'}</p>
                        {renderDbCalendarActions(event)}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Paciente</TableHead>
                <TableHead>Edad</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead>Origen</TableHead>
                <TableHead>Fecha y Hora</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No hay citas registradas
                  </TableCell>
                </TableRow>
              ) : (
                appointments.map((appointment) => (
                  <TableRow key={appointment.id} data-testid={`appointment-row-${appointment.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{appointment.patient_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm whitespace-nowrap">
                      {appointment.patient_age != null && appointment.patient_age !== ''
                        ? `${appointment.patient_age} años`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {appointment.patient_email || '—'}
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {appointment.patient_phone}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal whitespace-nowrap">
                        {appointment.booking_source === 'admin'
                          ? 'Panel admin'
                          : appointment.booking_source === 'whatsapp'
                            ? 'WhatsApp'
                            : 'Sitio web'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <CalendarIconLucide className="w-4 h-4 text-primary" />
                          <span>
                            {formatAppointmentDateOnlyInClinic(appointment.date, 'EEE, d MMM yyyy')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          <span>{appointment.time}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {appointment.reason || 'Sin especificar'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(appointment.status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {appointment.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-success hover:text-success hover:bg-success/10"
                              onClick={() => handleStatusChange(appointment.id, 'confirmed')}
                              data-testid={`confirm-appointment-${appointment.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Confirmar
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-accent hover:text-accent hover:bg-accent/10"
                              onClick={() => openCancelDialog(appointment.id, appointment.patient_name)}
                              data-testid={`cancel-appointment-${appointment.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                          </>
                        )}
                        {appointment.status === 'confirmed' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-accent hover:text-accent hover:bg-accent/10"
                            onClick={() => openCancelDialog(appointment.id, appointment.patient_name)}
                            data-testid={`cancel-appointment-${appointment.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancelar
                          </Button>
                        )}
                        {(appointment.status === 'pending' || appointment.status === 'confirmed') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-primary hover:text-primary hover:bg-primary/10"
                            onClick={() => handleStatusChange(appointment.id, 'completed')}
                            data-testid={`complete-appointment-${appointment.id}`}
                          >
                            <CircleCheck className="w-4 h-4 mr-1" />
                            Completada
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open);
          if (!open) setCancelTarget({ id: null, patientName: '' });
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancelar cita</DialogTitle>
            <DialogDescription>
              Si escribes un mensaje y el paciente tiene teléfono, se enviará por WhatsApp al guardar. Puedes dejar el
              texto vacío para cancelar sin notificación.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-wa-message">Mensaje para el paciente (WhatsApp)</Label>
            <Textarea
              id="cancel-wa-message"
              rows={5}
              value={cancelMessage}
              onChange={(e) => setCancelMessage(e.target.value)}
              className="resize-y min-h-[120px]"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelSubmitting}>
              Volver
            </Button>
            <Button type="button" variant="destructive" onClick={submitCancelAppointment} disabled={cancelSubmitting}>
              {cancelSubmitting ? 'Cancelando…' : 'Cancelar cita'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
